import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Rocket, Upload, Download, Loader2, CheckCircle2, Undo2, Coins, Globe, ShieldAlert, Sparkles, Wallet, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import TopUpDialog from '../../components/credits/TopUpDialog';

// Rotating shimmer messages shown while the AI import (upload) is running so the
// wait reads as progress rather than a frozen spinner.
const UPLOAD_MESSAGES = [
  'Reading your file…',
  'Checking data volume…',
  'Breaking into chunks…',
  'Mapping columns with AI…',
  'Matching against existing records…',
  'Almost there…',
];

const ENTITY_TABS = [
  { key: 'property', label: 'Properties', cols: ['name', 'address', 'city', 'state', 'zip', 'country', 'property_code', 'property_type', 'legal_entity_name', 'ein'] },
  { key: 'unit', label: 'Units', cols: ['unit_number', 'property_name', 'address', 'city', 'state', 'zip', 'country', 'pms_code', 'gl_code', 'commodity'] },
  { key: 'vendor_account', label: 'Vendor Accounts', cols: ['vendor_name', 'account_number', 'commodity', 'account_name', 'property_name', 'service_address', 'account_city', 'account_state', 'account_country', 'account_postal_code', 'frequency', 'bill_source', 'gl_code', 'parent_account_number'] },
  { key: 'meter', label: 'Meters', cols: ['meter_number', 'commodity', 'account_number', 'property_name', 'gl_code', 'address', 'city', 'state', 'country', 'postal_code'] },
  { key: 'credential', label: 'Credentials', cols: ['vendor_name', 'account_name', 'account_number', 'portal_url', 'username'] },
  { key: 'entity', label: 'Entities', cols: ['identifier_number', 'utility_type', 'property_name', 'gl_code', 'address', 'city', 'state', 'country', 'postal_code'] },
];

const STEPS = ['Plan', 'Import', 'Review', 'Onboarded'];
const stepIndex = (s) => (s === 'complete' ? 3 : s === 'applying' ? 2 : s === 'review' ? 2 : s === 'in_progress' ? 1 : 0);

export default function MigrationJourneyDetail() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const fileRef = useRef(null);
  const [journey, setJourney] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState('property');
  const [credits, setCredits] = useState(null);
  const [busy, setBusy] = useState(null); // 'upload' | 'approve' | 'rollback'
  const [scrapeRun, setScrapeRun] = useState(null);
  const [showScrape, setShowScrape] = useState(false);
  const [cred, setCred] = useState({ portal_url: '', username: '', password: '' });
  const [twofa, setTwofa] = useState('');
  const [wallet, setWallet] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [onboardProgress, setOnboardProgress] = useState(null); // {phase,label,done,total,pct}
  const [uploadMsg, setUploadMsg] = useState(UPLOAD_MESSAGES[0]);
  const pollRef = useRef(null);

  const params = { params: { account_id: account?.id } };

  const loadStaging = useCallback((entityType) => {
    if (!account?.id) return;
    api.get(`/ubm-migration/journeys/${journeyId}/staging`, {
      params: { account_id: account?.id, entity_type: entityType },
    })
      .then(({ data }) => { setRows(data.rows || []); setCounts(data.counts || {}); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, account?.id]);

  const load = useCallback(() => {
    if (!account?.id) return;
    api.get(`/ubm-migration/journeys/${journeyId}`, params)
      .then(({ data }) => { setJourney(data); setCounts(data.counts || {}); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load journey'));
    // Staging rows load PER entity type (loadStaging effect) so a large
    // migration never truncates one type against the global row cap.
    api.get(`/ubm-migration/journeys/${journeyId}/credits`, params)
      .then(({ data }) => setCredits(data)).catch(() => {});
    api.get(`/ubm-migration/journeys/${journeyId}/runs`, params)
      .then(({ data }) => {
        const active = (data.scrape_runs || []).find((r) => ['queued', 'running', 'awaiting_2fa'].includes(r.status));
        setScrapeRun(active || (data.scrape_runs || [])[0] || null);
      }).catch(() => {});
    api.get('/credits/wallet', params)
      .then(({ data }) => setWallet(data)).catch(() => setWallet(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, account?.id]);

  useEffect(() => { load(); }, [load]);

  // Fetch staging rows for the selected entity tab (initial + on tab change).
  useEffect(() => { loadStaging(tab); }, [tab, loadStaging]);

  // Live-poll while a scrape bot is active.
  useEffect(() => {
    if (!scrapeRun || !['queued', 'running', 'awaiting_2fa'].includes(scrapeRun.status)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [scrapeRun, load]);

  // Rotate the upload shimmer message while an AI import is running.
  useEffect(() => {
    if (busy !== 'upload') return;
    let i = 0;
    setUploadMsg(UPLOAD_MESSAGES[0]);
    const t = setInterval(() => {
      i = (i + 1) % UPLOAD_MESSAGES.length;
      setUploadMsg(UPLOAD_MESSAGES[i]);
    }, 2200);
    return () => clearInterval(t);
  }, [busy]);

  // Clear any onboarding poll timer on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startScrape = async () => {
    if (!cred.portal_url || !cred.username || !cred.password) { toast.error('Portal URL, username and password are required'); return; }
    setBusy('scrape');
    try {
      const { data } = await api.post(`/ubm-migration/journeys/${journeyId}/scrape`, cred, params);
      toast.success('Sample scrape started');
      setShowScrape(false);
      setCred({ portal_url: '', username: '', password: '' });
      setScrapeRun({ id: data.run_id, status: data.status, progress: { message: 'Starting…' } });
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start scrape');
    } finally { setBusy(null); }
  };

  const submit2fa = async () => {
    if (!twofa.trim() || !scrapeRun) return;
    try {
      await api.post(`/ubm-migration/runs/${scrapeRun.id}/2fa`, { code: twofa.trim() }, params);
      toast.success('Verification code sent — resuming scrape');
      setTwofa('');
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit code');
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/ubm-migration/journeys/${journeyId}/files`, form,
        { ...params, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      toast.success(`Staged ${data.staged} records from ${file.name}`);
      load(); loadStaging(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleRow = async (row) => {
    const included = !(row.review?.included ?? true);
    try {
      await api.patch(`/ubm-migration/journeys/${journeyId}/staging/${row.id}`, { included }, params);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, review: { ...r.review, included } } : r)));
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/ubm-migration/journeys/${journeyId}/export.xlsx`,
        { ...params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `migration_review_${journeyId.slice(0, 8)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const approve = async () => {
    if (!window.confirm('Onboard all included NEW records into UBM? This is reversible.')) return;
    setBusy('approve');
    setOnboardProgress({ phase: 'analyzing', label: 'Checking data volume…', done: 0, total: counts.all?.new || 0, pct: 0 });

    // Poll the journey for live onboarding progress (the approve request itself
    // can outlast the proxy timeout on big imports, so polling is the source of
    // truth for progress + completion).
    const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    stopPoll();
    let finished = false;
    const finishSuccess = (j) => {
      finished = true; stopPoll(); setOnboardProgress(null);
      const c = (j?.result?.created) || {};
      toast.success(`Onboarded ${c.locations || 0} properties, ${c.spaces || 0} units, ${c.vendor_accounts || 0} accounts, ${c.meters || 0} meters${c.credentials ? `, ${c.credentials} credentials` : ''}`);
      if (j) { setJourney(j); setCounts(j.counts || {}); }
      load(); loadStaging(tab);
    };
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/ubm-migration/journeys/${journeyId}`, params);
        if (j?.onboard_progress) setOnboardProgress(j.onboard_progress);
        if (j?.status === 'complete' && !finished) { finishSuccess(j); setBusy(null); }
      } catch { /* keep polling */ }
    }, 1500);

    try {
      // Generous timeout so the browser waits for the server on large imports.
      const { data } = await api.post(`/ubm-migration/journeys/${journeyId}/approve`, {}, { ...params, timeout: 600000 });
      if (data?.background) {
        // Large import: server keeps working. Keep the progress card + poller
        // alive; the user may safely close the tab (we notify them when done).
        toast.info('Onboarding started — this runs in the background, so you can keep working or close this tab. We\u2019ll notify you when it\u2019s done.');
        return; // busy stays 'approve'; poller finishes it
      }
      if (!finished) {
        finished = true; stopPoll(); setOnboardProgress(null);
        const c = data.created || {};
        toast.success(data.already_applied ? 'Already onboarded' :
          `Onboarded ${c.locations || 0} properties, ${c.spaces || 0} units, ${c.vendor_accounts || 0} accounts, ${c.meters || 0} meters${c.credentials ? `, ${c.credentials} credentials` : ''}`);
        load(); loadStaging(tab);
      }
    } catch (e) {
      // Onboarding runs at-most-once on the server (atomic claim). On a timeout
      // or duplicate-rejection, re-check the journey before reporting failure.
      if (finished) { setBusy(null); return; }
      try {
        const { data: j } = await api.get(`/ubm-migration/journeys/${journeyId}`, params);
        if (j?.status === 'complete') { finishSuccess(j); setBusy(null); return; }
        if (j?.status === 'applying') {
          // Keep the progress card + poller alive; it will finish on its own.
          if (j?.onboard_progress) setOnboardProgress(j.onboard_progress);
          toast.info('Onboarding is processing — this can take a minute for large imports. Keep this tab open.');
          return;
        }
      } catch { /* fall through */ }
      finished = true; stopPoll(); setOnboardProgress(null);
      toast.error(e.response?.data?.detail || 'Onboard failed');
    } finally { if (finished) setBusy(null); }
  };

  const rollback = async () => {
    if (!window.confirm('Undo this onboarding batch? All records created by it will be removed.')) return;
    setBusy('rollback');
    try {
      await api.post(`/ubm-migration/journeys/${journeyId}/rollback`, {}, params);
      toast.success('Rolled back');
      load(); loadStaging(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rollback failed');
    } finally { setBusy(null); }
  };

  if (!journey) {
    return (
      <MainLayout>
        <div className="text-sm text-slate-500 inline-flex items-center gap-2 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading journey…</div>
      </MainLayout>
    );
  }

  const active = stepIndex(journey.status);
  const created = journey.result?.created || {};

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-4" data-testid="migration-journey-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate('/ubm/migration')} data-testid="migration-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Rocket className="h-5 w-5 text-orange-600" />
            Migration — {journey.plan?.source_platform || 'Untitled'}
          </h1>
          <Badge variant="outline" className="rounded-none capitalize" data-testid="migration-status-badge">{(journey.status || '').replace('_', ' ')}</Badge>
          {credits && credits.total_credits > 0 && (
            <span className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1" data-testid="migration-credits-used">
              <Coins className="h-3.5 w-3.5 text-amber-500" /> {credits.total_credits} credits used (≈ ${(credits.total_credits * 0.10).toFixed(2)}, preview)
            </span>
          )}
        </div>

        {/* Stepper — reflects live progress: the active stage pulses and its
            in-bound connector fills so the shimmer aligns to Plan/Import/Review/Onboarded. */}
        {(() => {
          const onboarding = busy === 'approve' || journey.status === 'applying';
          const importing = busy === 'upload';
          const busyStep = onboarding ? 3 : importing ? 1 : -1;
          const pct = onboardProgress?.pct ?? 0;
          return (
            <div className="flex items-center gap-2 max-w-2xl" data-testid="migration-stepper">
              {STEPS.map((s, i) => {
                const done = i < active || (journey.status === 'complete' && i === 3);
                const isBusy = i === busyStep;
                return (
                  <React.Fragment key={s}>
                    <div className={`flex items-center gap-1.5 text-xs ${isBusy ? 'text-orange-700 font-semibold animate-pulse' : i <= active ? 'text-orange-700 font-semibold' : 'text-slate-400'}`}>
                      <span className={`h-5 w-5 rounded-full text-[10px] flex items-center justify-center ${
                        done ? 'bg-emerald-500 text-white'
                        : isBusy ? 'bg-orange-600 text-white ring-2 ring-orange-300 ring-offset-1'
                        : i === active ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : done ? '✓' : i + 1}
                      </span>
                      {s}
                    </div>
                    {i < STEPS.length - 1 && (
                      (onboarding && i === 2) ? (
                        <div className="h-1 flex-1 rounded-full bg-slate-200 overflow-hidden" data-testid="migration-stepper-onboard-connector">
                          <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      ) : (importing && i === 0) ? (
                        <div className="h-1 flex-1 rounded-full bg-slate-200 overflow-hidden relative">
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_infinite] bg-gradient-to-r from-transparent via-orange-400 to-transparent" />
                        </div>
                      ) : (
                        <div className={`h-px flex-1 ${i < active ? 'bg-orange-400' : 'bg-slate-200'}`} />
                      )
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          );
        })()}

        {journey.status === 'complete' && (
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="migration-detail-complete-banner">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-emerald-800">Migration onboarded</div>
              <div className="text-slate-700">{created.locations || 0} properties, {created.spaces || 0} units, {created.vendor_accounts || 0} vendor accounts, {created.meters || 0} meters created in UBM.</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={rollback} disabled={busy === 'rollback'} data-testid="migration-rollback-btn">
              {busy === 'rollback' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Undo2 className="h-3.5 w-3.5 mr-1" />} Undo batch
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="migration-file-input" />
          {journey.status !== 'complete' && (
            <Button variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} disabled={busy === 'upload'} data-testid="migration-upload-btn">
              {busy === 'upload' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              {busy === 'upload' ? uploadMsg : 'Upload export (XLS/CSV)'}
            </Button>
          )}
          {journey.status !== 'complete' && (
            <Button variant="outline" className="rounded-none" onClick={() => setShowScrape(true)}
              disabled={scrapeRun && ['queued', 'running', 'awaiting_2fa'].includes(scrapeRun.status)} data-testid="migration-scrape-btn">
              <Globe className="h-4 w-4 mr-1" /> Scrape incumbent portal
            </Button>
          )}
          {rows.length > 0 && (
            <Button variant="outline" className="rounded-none" onClick={downloadXlsx} data-testid="migration-download-xlsx-btn">
              <Download className="h-4 w-4 mr-1" /> Download XLSX
            </Button>
          )}
          {journey.status === 'review' && (counts.all?.included || 0) > 0 && (
            <Button className="rounded-none bg-orange-600 hover:bg-orange-700 ml-auto" onClick={approve} disabled={busy === 'approve'} data-testid="migration-approve-btn">
              {busy === 'approve' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Approve &amp; Onboard {counts.all?.new || 0} new records
            </Button>
          )}
        </div>

        {/* Combined onboarding shimmer + live progress bar (one cohesive card) */}
        {(busy === 'approve' || journey.status === 'applying' || onboardProgress) && (
          <div className="rounded-md border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-4 overflow-hidden relative" data-testid="migration-onboard-progress">
            {/* moving shimmer sheen */}
            <div className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-white/50 to-transparent" />
            <div className="relative flex items-center gap-3">
              <div className="relative">
                <Database className="h-5 w-5 text-orange-600" />
                <Loader2 className="h-5 w-5 text-orange-400 animate-spin absolute inset-0 opacity-40" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-orange-900 text-sm truncate" data-testid="migration-onboard-phase">
                    {onboardProgress?.label || 'Onboarding your data…'}
                  </div>
                  {onboardProgress?.total > 0 && (
                    <div className="text-xs font-medium text-orange-700 whitespace-nowrap" data-testid="migration-onboard-count">
                      {(onboardProgress.done || 0).toLocaleString()} of {(onboardProgress.total || 0).toLocaleString()} records
                    </div>
                  )}
                </div>
                <Progress
                  value={onboardProgress?.total > 0 ? (onboardProgress.pct ?? Math.round((onboardProgress.done / onboardProgress.total) * 100)) : undefined}
                  className={`mt-2 h-2 bg-orange-100 ${onboardProgress?.total > 0 ? '' : 'animate-pulse'}`}
                  data-testid="migration-onboard-bar"
                />
                <div className="mt-1.5 text-[11px] text-orange-700/80">
                  Large imports are processed in chunks to avoid duplicates — keep this tab open, it continues even if it looks slow.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live scrape-bot status */}
        {scrapeRun && ['queued', 'running', 'awaiting_2fa', 'timeout', 'failed'].includes(scrapeRun.status) && (
          <div className={`rounded-md border p-3 ${scrapeRun.status === 'awaiting_2fa' ? 'border-amber-300 bg-amber-50' : scrapeRun.status === 'timeout' || scrapeRun.status === 'failed' ? 'border-rose-200 bg-rose-50' : 'border-blue-200 bg-blue-50'}`} data-testid="migration-scrape-status">
            <div className="flex items-center gap-2 text-sm">
              {['queued', 'running'].includes(scrapeRun.status) && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
              {scrapeRun.status === 'awaiting_2fa' && <ShieldAlert className="h-4 w-4 text-amber-600" />}
              <span className="font-medium capitalize">{scrapeRun.status.replace('_', ' ')}</span>
              <span className="text-muted-foreground">— {scrapeRun.progress?.message}</span>
            </div>
            {scrapeRun.status === 'awaiting_2fa' && (
              <div className="flex gap-2 mt-2 max-w-sm">
                <Input value={twofa} onChange={(e) => setTwofa(e.target.value)} placeholder="Enter verification code" data-testid="migration-2fa-input" />
                <Button className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={submit2fa} data-testid="migration-2fa-submit">Submit code</Button>
              </div>
            )}
          </div>
        )}

        {/* Quote-gate: after sample scrape completes, show the full-migration credit quote + top-up if insufficient */}
        {scrapeRun && scrapeRun.status === 'completed' && scrapeRun.result?.quote && (() => {
          const q = scrapeRun.result.quote;
          const bal = wallet?.balance_credits ?? 0;
          const insufficient = bal < q.est_credits;
          const shortfall = Math.max(0, Math.ceil(q.est_credits - bal));
          return (
            <div
              className={`rounded-md border p-3 ${insufficient ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
              data-testid="migration-quote-gate"
            >
              <div className="flex items-start gap-2">
                <Sparkles className={`h-5 w-5 shrink-0 mt-0.5 ${insufficient ? 'text-amber-600' : 'text-emerald-600'}`} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800" data-testid="quote-headline">
                    Sample scrape complete — full-migration estimate
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    <span className="font-semibold">{q.entities}</span> entities discovered · full scrape est.{' '}
                    <span className="font-semibold" data-testid="quote-credits">{q.est_credits} credits</span>{' '}
                    (≈ <span data-testid="quote-usd">${q.est_usd?.toFixed?.(2) ?? q.est_usd}</span>)
                  </div>
                  <div className="text-xs mt-1 inline-flex items-center gap-1 text-slate-700">
                    <Wallet className="h-3.5 w-3.5 text-amber-500" />
                    Your wallet: <span className="font-semibold" data-testid="quote-wallet-balance">{bal.toFixed(1)} credits</span>
                    {insufficient && (
                      <span className="text-amber-700 font-semibold ml-1" data-testid="quote-shortfall">
                        — need +{shortfall} more
                      </span>
                    )}
                  </div>
                </div>
                {insufficient ? (
                  <Button
                    className="rounded-none bg-amber-600 hover:bg-amber-700"
                    onClick={() => setTopupOpen(true)}
                    data-testid="quote-topup-btn"
                  >
                    <Coins className="h-4 w-4 mr-1" /> Top up credits
                  </Button>
                ) : (
                  <Badge variant="outline" className="rounded-none border-emerald-500 text-emerald-700 self-center" data-testid="quote-ok-badge">
                    ✓ Sufficient credits
                  </Badge>
                )}
              </div>
            </div>
          );
        })()}

        {/* Entity tabs */}
        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed p-10 text-center text-sm text-slate-500" data-testid="migration-no-staged">
            Nothing staged yet — upload your platform export above, or ask the AI assistant to run the import from chat.
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-none">
              {ENTITY_TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="rounded-none" data-testid={`migration-tab-${t.key}`}>
                  {t.label} ({counts[t.key]?.total || 0})
                </TabsTrigger>
              ))}
            </TabsList>
            {ENTITY_TABS.map((t) => (
              <TabsContent key={t.key} value={t.key}>
                <div className="border rounded-md overflow-x-auto bg-white">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-left">
                        <th className="p-2 w-10">Incl.</th>
                        {t.cols.map((c) => <th key={c} className="p-2 capitalize whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}
                        <th className="p-2">Dedupe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.filter((r) => r.entity_type === t.key).map((r) => (
                        <tr key={r.id} className={`border-t ${!(r.review?.included ?? true) ? 'opacity-40' : ''}`} data-testid={`migration-row-${r.id}`}>
                          <td className="p-2">
                            <input type="checkbox" checked={r.review?.included ?? true}
                              onChange={() => toggleRow(r)} disabled={journey.status === 'complete'}
                              data-testid={`migration-row-include-${r.id}`} />
                          </td>
                          {t.cols.map((c) => <td key={c} className="p-2 whitespace-nowrap">{r.data?.[c] || <span className="text-slate-300">—</span>}</td>)}
                          <td className="p-2">
                            {r.dedupe?.status === 'exists'
                              ? <Badge variant="outline" className="rounded-none border-blue-400 text-blue-600 text-[10px]">exists</Badge>
                              : <Badge variant="outline" className="rounded-none border-emerald-400 text-emerald-600 text-[10px]">new</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        <Dialog open={showScrape} onOpenChange={setShowScrape}>
          <DialogContent data-testid="migration-scrape-dialog">
            <DialogHeader><DialogTitle>Scrape incumbent portal</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Our AI bot will log in and run a <strong>free sample scrape</strong> (~5 properties) to prove it can read your platform, then estimate the full migration. Credentials are encrypted and never shown again.
              </p>
              <label className="text-sm font-medium">Portal login URL</label>
              <Input value={cred.portal_url} onChange={(e) => setCred({ ...cred, portal_url: e.target.value })} placeholder="https://portal.yourprovider.com/login" data-testid="migration-scrape-url" />
              <label className="text-sm font-medium">Username</label>
              <Input value={cred.username} onChange={(e) => setCred({ ...cred, username: e.target.value })} autoComplete="off" data-testid="migration-scrape-user" />
              <label className="text-sm font-medium">Password</label>
              <Input type="password" value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} autoComplete="new-password" data-testid="migration-scrape-pass" />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setShowScrape(false)}>Cancel</Button>
              <Button className="rounded-none bg-orange-600 hover:bg-orange-700" onClick={startScrape} disabled={busy === 'scrape'} data-testid="migration-scrape-start">
                {busy === 'scrape' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Start sample scrape
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TopUpDialog open={topupOpen} onOpenChange={setTopupOpen} wallet={wallet} onOpened={load} />
      </div>
    </MainLayout>
  );
}
